/*
 * TunePad
 *
 * Michael S. Horn
 * Northwestern University
 * michael-horn@northwestern.edu
 *
 * This project was funded by the National Science Foundation (grant DRL-1612619).
 * Any opinions, findings and conclusions or recommendations expressed in this
 * material are those of the author(s) and do not necessarily reflect the views
 * of the National Science Foundation (NSF).
 */
import styles from './timeline.module.css' with { type: 'css' };
import colors from '../../definitions.module.css' with { type: 'css' };
import html from './midi.module.html';
import { MusicTrace, TraceEvent, TunePadAudio, toNum } from '../../audio';


/**
 * Timeline that shows midi piano roll
 * 
 * <timeline-midi
 *     playhead = "0"           // location of the playhead in beats
 *     theme = "dark | light"   // default is dark
 *     instrument = "piano">    // changes the color of the visualization
 */
export class TimelineMidi extends HTMLElement {

    static ELEMENT = "timeline-midi";


    static observedAttributes = [
        "playhead",    // location of the playhead in beats
        "theme",       // "dark" | "light"
        "instrument",  // changes the color of the waveform or trace (TimelineInstruments)
    ];


    // all of the HTML elements for the instrument are contained within a shadow DOM
    protected root : ShadowRoot;

    // TunePad audio
    protected audio : TunePadAudio;

    // Outer container for the timeline SVG element
    protected svg : SVGSVGElement;

    // Div containers
    protected container : HTMLDivElement;
    protected scrollContainer : HTMLDivElement;

    // total beat count for the current trace rounded up to the nearest measure
    protected get beats() {
        const b = this.trace.beats;
        const bars = Math.ceil(b / this.measure);
        return Math.max(this.measure, bars * this.measure);
    }

    /** duration of each measure in beats */
    protected get measure() { return this.audio.beatsPerMeasure; }

    /** dimensions of outer container (will get resized on compontent load) */
    protected width = 700;
    protected height = 150;

    /** how many pixels wide per quarter note */
    protected get beatWidth() { return Math.max(40, this.width / this.beats); }

    /** how many pixels wide for the entire trace */
    protected get traceWidth() { return this.beatWidth * this.beats; }

    /** pixels per second of audio */
    protected get pixelsPerSecond() { return this.beatWidth * (this.audio.bpm / 60); }
    
    /** height of the top ruler */
    protected readonly rulerHeight = 20;

    /** height of the element minus the ruler */
    protected get scoreHeight() { return this.height - this.rulerHeight; }

    /** trace object used to visualize audio data */
    protected trace : MusicTrace = MusicTrace.emptyTrace();

    /** playhead attribute: position of the orange playhead in beats */
    protected playhead = 0;

    /** path that renders the playhead and handles user input events */
    protected _playheadLine : SVGPathElement;

    /** waveform is rendered inside this group */
    protected _dataGroup : SVGGElement;
    
    /** for the midi roll, how many vertical notes to show? */
    private tracks = 32;

    private get isPercussion() : boolean { return this.getAttribute('instrument')?.startsWith('drum') || false; }



    constructor() {
        super();
        this.root = this.attachShadow({ mode: 'open' });
        this.root.adoptedStyleSheets.push(styles);
        this.root.adoptedStyleSheets.push(colors);
        this.root.innerHTML = html;

        this.container = this.root.querySelector('#container') as HTMLDivElement;
        this.scrollContainer = this.root.querySelector('#scroll-container') as HTMLDivElement;
        this.svg = this.root.querySelector('#timeline') as SVGSVGElement;
        this._playheadLine = document.createElementNS("http://www.w3.org/2000/svg", 'path');
        this._dataGroup = document.createElementNS("http://www.w3.org/2000/svg", 'g');
         
        this.audio = TunePadAudio.init();
        this.audio.addSubscriber(this);
    }

    public setTrace(t : MusicTrace, force : boolean = false) {
        const changed = !(t.isEquivalent(this.trace) && t.patch === this.trace.patch);
        this.trace = t;
        if (changed || force) this.render();
    }

    onClockReset() { }
    onClockTimeChange() { }
    onTempoChange() { }
    onTimeSignatureChange() { this.render(); }
    onKeyChange() { }


    connectedCallback() {
        this.registerTouchEvents();
        window.addEventListener('resize', (e) => this.render());
        this.render();
    }


    disconnectedCallback() {
        //console.log("Custom element removed from page.");
    }


    attributeChangedCallback(name : string, oldValue : string, newValue : string) {
        if (oldValue === newValue) return;
        if (name === 'playhead') {
            this.movePlayhead(toNum(newValue, this.beats));
        }
        else if (name === 'theme' && ['dark', 'light', 'bitcrush'].includes(newValue)) {
            this.root.querySelector('#container')?.classList.remove(oldValue);
            this.root.querySelector('#container')?.classList.add(newValue);
        }
        else if (name === 'instrument') {
            this.root.querySelector('#container')?.classList.remove(oldValue);
            this.root.querySelector('#container')?.classList.add(newValue);
        }
    }


    beatToX(beat : number) : number {
        return beat * this.beatWidth;
    }

    xToBeat(x : number) : number {
        let p = this.svg.createSVGPoint();
        p.x = x;
        p = p.matrixTransform(this.svg.getScreenCTM()!.inverse());
        return p.x / this.beatWidth;
    }


    /**
     * (Re)draw the entire timeline
     */
    protected render() {
        this.resize();
        this.svg.innerHTML = '';

        const group = document.createElementNS("http://www.w3.org/2000/svg", 'g');
        const ruler = this.renderRuler();
        group.append(ruler);
        this._dataGroup.innerHTML = '';
        group.append(this._dataGroup);
        this.renderPlayhead();
        group.append(this._playheadLine);
        this.svg.append(group);
        this.renderData();
    }


    private renderRuler() : SVGGElement {
        const beatNumbers = document.createElementNS("http://www.w3.org/2000/svg", 'g');

        // background rect
        const beatBacker = document.createElementNS("http://www.w3.org/2000/svg", 'rect');
        beatBacker.classList.add('ruler');
        beatBacker.setAttribute('x', `${this.beatToX(0)}`);
        beatBacker.setAttribute('y', '0');
        beatBacker.setAttribute('width', `${this.traceWidth}`);
        beatBacker.setAttribute('height', `${this.rulerHeight}`);

        beatNumbers.append(beatBacker);

        beatBacker.addEventListener('pointerdown', (e) => {
            this._down = true;
            this.movePlayhead(this.xToBeat(e.clientX));
            this.emitEvent('playhead-reposition');
        });

    
        for (let i = 1; i <= this.beats; i++) {
            let x = this.beatToX(i - 1);
            const text = document.createElementNS("http://www.w3.org/2000/svg", 'text');
            text.classList.add('ruler-marks');
            text.setAttribute('x', `${x + 5}`);
            text.setAttribute('y', `${this.rulerHeight / 2}`);
            text.innerHTML = (i === 1) ? "BEAT" : `${i}`;
            beatNumbers.append(text);

            if (i === 1) continue;

            const line = document.createElementNS("http://www.w3.org/2000/svg", 'line');
            line.classList.add('beat-line');
            line.setAttribute('x1', `${x}`);
            line.setAttribute('y1', '0');
            line.setAttribute('x2', `${x}`);
            line.setAttribute('y2', `${this.height}`);
    
            // make first beat of each measure thicker
            if ((i - 1) % this.measure === 0) {
                line.classList.add('measure');
            }
    
            beatNumbers.append(line);
        }
        return beatNumbers;
    }


    private renderPlayhead() {
        const h = Math.max(100, this.height);
        const rh = this.rulerHeight;
        const rh2 = this.rulerHeight - 5;
        const px = this.beatToX(this.playhead);
        this._playheadLine.setAttribute("d",
            `M ${px - 1} ${h} l 0 -${h - rh} l -6 -6 l 0 -${rh2} l 14 0 l 0 ${rh2} l -6 6 l 0 ${h} Z`);
        this._playheadLine.setAttribute("class", "playhead");
    }


    private noteToTrack(note : number) : number {
        const base = (this.isPercussion) ? 0 : this.trace.minNote;
        return (this.tracks - (Math.round(note - base) % this.tracks)) - 1;
    }


    protected renderData() {
        const trace = this.trace;
        const range = (trace.maxNote - trace.minNote) + 1;
        this.tracks = Math.max(12, range);
        this._dataGroup.setAttribute(
            "transform",
            `translate(0, ${this.rulerHeight}), ` +
            `scale(${this.beatWidth}, ${this.scoreHeight / this.tracks})`
        );
        const events = trace.filter(e => (
            e.command === TraceEvent.PLAY ||
            e.command === TraceEvent.SAMPLE
        ));
        for (const t of events) {
            this._dataGroup.append(this.renderNote(t));
        }
    }


    private renderNote(t : TraceEvent) : SVGRectElement {
        let down = false;

        const epsilon = 1/64;
        const rect = document.createElementNS("http://www.w3.org/2000/svg", 'rect') as SVGRectElement;
        rect.classList.add('timeline-mark', 'timeline-note');

        rect.setAttribute('width', `${t.duration - epsilon}`);
        rect.setAttribute('height', `${1 - epsilon}`);
        rect.setAttribute('x', `${t.time}`);
        rect.setAttribute('y', `${this.noteToTrack(t.note.note)}`);
        rect.setAttribute('data-tevent', `${t.id}`);

        rect.addEventListener('pointerenter', e => { 
            this.previewNote(t);
            this.emitEvent('note-enter', t); 
        });
        rect.addEventListener('pointerdown', e => { down = true; this.emitEvent('note-down', t); });
        rect.addEventListener('pointerup', e => { down = false; this.emitEvent('note-up', t); });
        rect.addEventListener('pointerleave', e => { 
            this.hidePreview();
            this.emitEvent('note-leave', t);
            down = false;
        });
        return rect;
    }


    /**
     * Called when the window and possibly containing element are resized.
     */
    private resize() {
        this.width = this.root.host.getBoundingClientRect().width;
        this.height = this.root.host.getBoundingClientRect().height;
        this.scrollContainer.style.width = `${this.width}px`;
        this.container.style.width = `${this.traceWidth}px`;
        //this.container.style.height = `${this.height}px`;
        this.svg.setAttribute("viewBox", `0 0 ${this.traceWidth} ${this.height}`);
    }


    /**
     * Move playhead to a new location
     */
    private movePlayhead(p : number) {
        this.playhead = Math.max(0, Math.min(this.beats, p));
        this.renderPlayhead();
        if (!this._down) {
            this.scrollContainer.scrollTo(this.beatToX(p) - this.width * 0.8, 0);
        }

        this.root.querySelectorAll('.timeline-note').forEach(tn => { 
            tn.classList.remove('highlight');
        });
        if (p > 0) { 
            this.trace.slice(p).forEach(te => {
                this.root.querySelector(`.timeline-note[data-tevent="${te.id}"]`)?.classList.add('highlight');
            });
        }
    }

    private hidePreview() {
        this.root.querySelector('#note-hint')?.classList.remove('show');
    }

    private previewNote(t : TraceEvent) {
        const key = this.audio.key;
        const name = key.getNoteName(t.note, false);
        const acc = key.getAccidental(t.note);
        const tt = Number.isInteger(t.note.note) ? `${t.note.note}` : t.note.note.toFixed(2);
        const hint = this.root.querySelector('#note-hint');
        if (hint) {
            hint.querySelector('.note-name')!.innerHTML = name;
            hint.querySelector('.accidental')!.innerHTML = acc ? acc : '&nbsp';
            hint.querySelector('.octave')!.innerHTML = `${t.note.octave - 1}`;
            hint.querySelector('.note-info')!.innerHTML = `Note ${tt}`;
            hint.querySelector('.note-info')!.innerHTML += ` (line ${t.line})`;
            hint.classList.add('show');
        }
    }

    emitEvent(name : string, tevent? : TraceEvent) {
        this.dispatchEvent(new CustomEvent(name, {
            bubbles: true,
            composed: true,
            detail: { playhead : this.playhead, note : tevent }
        }));
    }


    private _down = false;


    private registerTouchEvents() {
        this._playheadLine.addEventListener('pointerdown', (e) => {
            this.emitEvent('playhead-down');
            this._playheadLine.setPointerCapture(e.pointerId);
            this._down = true;
        });

        document.addEventListener('pointermove', (e) => {
            if (this._down) {
                this.movePlayhead(this.xToBeat(e.clientX));
                this.emitEvent('playhead-drag');
            }
        });

        document.addEventListener('pointerup', (e) => {
            if (this._down) {
                this.emitEvent('playhead-up');
            }
            this._down = false;
        });
    }
}
